import React, { useEffect, useState } from 'react';
import { Star, Copy, Loader2, AlertTriangle, X, Eye } from 'lucide-react';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit } from '../lib/embedChannel';

/**
 * Embed Marketplace (iframe surface).
 *
 * Mounted at `/embed/v2/marketplace?token=<jwt>`.
 *
 * A read-only grid of every published visual in the caller's tenant.
 * Featured items surface at the top with a ⭐ badge so new teams see
 * the best-of-tenant templates first. Callers with `write` cap get a
 * "Clone into my workspace" action on each tile.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authenticate(token) {
  const res = await fetch(`${API}/api/embed/v2/authenticate`, {
    method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`authenticate ${res.status}`);
  return res.json();
}

async function listMarketplace(token, q, tag) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (tag) params.set('tag', tag);
  const url = tag
    ? `${API}/api/embed/v2/marketplace/visuals-by-tag?${params.toString()}`
    : `${API}/api/embed/v2/marketplace/visuals${q ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
  if (!res.ok) throw new Error(`list ${res.status}`);
  return res.json();
}

async function listTags(token) {
  const res = await fetch(`${API}/api/embed/v2/marketplace/tags`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) return { data: [] };
  return res.json();
}

async function cloneVisual(token, visualId) {
  const res = await fetch(
    `${API}/api/embed/v2/marketplace/visuals/${encodeURIComponent(visualId)}/clone`,
    { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } },
  );
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.detail?.message || `clone ${res.status}`);
  }
  return res.json();
}

function VisualTile({ visual, canWrite, canAdmin, onClone, onToggleFeature, onPreview }) {
  const [cloning, setCloning] = useState(false);
  const [featuring, setFeaturing] = useState(false);
  return (
    <div
      className={`rounded-lg border ${visual.featured ? 'border-amber-500/50 bg-amber-500/[0.04]' : 'border-border/40 bg-background/50'} overflow-hidden flex flex-col`}
      data-testid={`embed-marketplace-tile-${visual.visual_id}`}
    >
      {visual.featured && (
        <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 text-amber-200 text-[10px] font-semibold uppercase tracking-wider" data-testid={`embed-marketplace-badge-featured-${visual.visual_id}`}>
          <Star className="w-3 h-3 fill-current" />
          Featured
        </div>
      )}
      <button
        type="button"
        onClick={() => onPreview?.(visual)}
        className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-indigo-400/60"
        data-testid={`embed-marketplace-preview-open-${visual.visual_id}`}
      >
        {visual.thumbnail_svg ? (
          <img src={visual.thumbnail_svg} alt="" className="w-full h-24 object-cover bg-muted/20 hover:opacity-80 transition-opacity" />
        ) : (
          <div className="w-full h-24 bg-muted/20 hover:bg-muted/30 transition-colors" />
        )}
      </button>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div>
          <div className="text-[12px] font-semibold truncate">{visual.name || visual.visual_id}</div>
          <div className="text-[10px] text-muted-foreground">
            {visual.viz_type || 'chart'}
            {typeof visual.clone_count === 'number' && ` · ${visual.clone_count} clone${visual.clone_count === 1 ? '' : 's'}`}
          </div>
          {Array.isArray(visual.tags) && visual.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {visual.tags.slice(0, 4).map((t) => (
                <span key={t} className="text-[9px] rounded bg-indigo-500/[0.10] border border-indigo-500/30 px-1.5 py-[1px] font-mono text-indigo-200">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPreview?.(visual)}
            className="inline-flex items-center gap-1 rounded-md border border-border/40 hover:bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
            data-testid={`embed-marketplace-preview-${visual.visual_id}`}
          >
            <Eye className="w-3 h-3" /> Preview
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={async () => { setCloning(true); try { await onClone(visual.visual_id); } finally { setCloning(false); } }}
              disabled={cloning}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-500/40 bg-indigo-500/[0.08] hover:bg-indigo-500/[0.16] px-2 py-1 text-[10px] text-indigo-200 disabled:opacity-40"
              data-testid={`embed-marketplace-clone-${visual.visual_id}`}
            >
              {cloning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
              {cloning ? 'Cloning…' : 'Clone'}
            </button>
          )}
          {canAdmin && (
            <button
              type="button"
              onClick={async () => { setFeaturing(true); try { await onToggleFeature(visual.visual_id, !visual.featured); } finally { setFeaturing(false); } }}
              disabled={featuring}
              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] transition-colors disabled:opacity-40 ${
                visual.featured
                  ? 'border border-amber-500/60 bg-amber-500/[0.16] hover:bg-amber-500/[0.28] text-amber-200'
                  : 'border border-border/40 hover:bg-muted/40 text-muted-foreground'
              }`}
              data-testid={`embed-marketplace-feature-${visual.visual_id}`}
              title={visual.featured ? 'Un-feature' : 'Feature this template'}
            >
              {featuring ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className={`w-3 h-3 ${visual.featured ? 'fill-current' : ''}`} />}
              {visual.featured ? 'Un-feature' : 'Feature'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ visual, canWrite, onClone, onClose }) {
  const [cloning, setCloning] = useState(false);
  if (!visual) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="embed-marketplace-preview-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-lg border border-border/60 bg-background shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 rounded-md p-1 hover:bg-muted/40 text-muted-foreground z-10"
          data-testid="embed-marketplace-preview-close"
          aria-label="Close preview"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            {visual.thumbnail_svg ? (
              <img src={visual.thumbnail_svg} alt="" className="w-40 h-40 rounded bg-muted/20 object-cover shrink-0" />
            ) : (
              <div className="w-40 h-40 rounded bg-muted/20 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-lg font-semibold truncate" data-testid="embed-marketplace-preview-name">
                  {visual.name || visual.visual_id}
                </div>
                {visual.featured && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 text-amber-200 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
                    <Star className="w-3 h-3 fill-current" /> Featured
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {visual.viz_type || 'chart'}
                {typeof visual.clone_count === 'number' && ` · ${visual.clone_count} clone${visual.clone_count === 1 ? '' : 's'}`}
              </div>
              {visual.published_by && (
                <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate">
                  Published by {visual.published_by}
                </div>
              )}
              {Array.isArray(visual.tags) && visual.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {visual.tags.map((t) => (
                    <span key={t} className="text-[10px] rounded bg-indigo-500/[0.12] border border-indigo-500/40 px-2 py-0.5 font-mono text-indigo-200">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {visual.description && (
            <div className="text-[12px] text-muted-foreground whitespace-pre-wrap border-t border-border/30 pt-3">
              {visual.description}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 border-t border-border/30 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border/40 px-3 py-1.5 text-[11px] hover:bg-muted/40"
              data-testid="embed-marketplace-preview-cancel"
            >
              Close
            </button>
            {canWrite && (
              <button
                type="button"
                onClick={async () => { setCloning(true); try { await onClone(visual.visual_id); onClose(); } finally { setCloning(false); } }}
                disabled={cloning}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-500 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40"
                data-testid="embed-marketplace-preview-clone"
              >
                {cloning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                {cloning ? 'Cloning…' : 'Clone into my workspace'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmbedMarketplacePage() {
  const token = useEmbedToken();
  const [claims, setClaims] = useState(null);
  const [branding, setBranding] = useState(null);
  const [visuals, setVisuals] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [tags, setTags] = useState([]);
  const [previewVisual, setPreviewVisual] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    listTags(token)
      .then((b) => { if (!cancelled) setTags(b?.data || []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authenticate(token)
      .then((c) => {
        if (cancelled) return;
        setClaims(c);
        setBranding(c?.branding || null);
        emit('viewer.identified', { sub: c?.sub, roles: c?.roles || [], capabilities: c?.capabilities || [] });
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    listMarketplace(token, query, activeTag)
      .then((b) => { if (!cancelled) setVisuals(b?.data || []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, query, activeTag]);

  const canWrite = (claims?.capabilities || []).includes('write');
  const canAdmin = (claims?.capabilities || []).includes('admin');

  const clone = async (visualId) => {
    try {
      const b = await cloneVisual(token, visualId);
      setToast(`Cloned into your workspace as ${b?.visual?.name || 'a new visual'}`);
      emit('marketplace.visual.cloned', { source_visual_id: visualId, new_visual_id: b?.visual?.visual_id });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast(`Clone failed: ${e.message}`);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const toggleFeature = async (visualId, featured) => {
    try {
      const res = await fetch(
        `${API}/api/embed/v2/marketplace/visuals/${encodeURIComponent(visualId)}/feature`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ featured }),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `feature ${res.status}`);
      }
      setVisuals((prev) => {
        const updated = prev.map((v) => (v.visual_id === visualId ? { ...v, featured } : v));
        return [...updated].sort((a, b) => (Number(b.featured || 0) - Number(a.featured || 0)));
      });
      setToast(featured ? 'Featured — this template now surfaces first' : 'Un-featured');
      emit('marketplace.visual.featured', { visual_id: visualId, featured });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast(`Feature failed: ${e.message}`);
      setTimeout(() => setToast(null), 4000);
    }
  };

  if (!token) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Waiting for session token…</div>;
  }
  const productName = brandingProductName(branding);

  return (
    <div className="min-h-screen bg-background text-foreground p-4" style={brandingRootStyle(branding)} data-testid="embed-marketplace-root">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold" data-testid="embed-marketplace-product-name">
            {productName || 'Visual Marketplace'}
          </div>
          <div className="text-[10px] text-muted-foreground">
            Browse and clone published templates from your team
          </div>
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          className="w-40 bg-transparent border border-border/40 rounded-md px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
          data-testid="embed-marketplace-search"
        />
      </header>

      {toast && (
        <div className="mb-3 rounded-md border border-indigo-500/40 bg-indigo-500/[0.12] px-3 py-2 text-[11px] text-indigo-100" data-testid="embed-marketplace-toast">
          {toast}
        </div>
      )}
      {tags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="embed-marketplace-tags">
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
              !activeTag
                ? 'border-indigo-400/70 bg-indigo-500/20 text-indigo-100'
                : 'border-border/40 bg-background/50 hover:border-indigo-400/40 text-muted-foreground'
            }`}
            data-testid="embed-marketplace-tag-all"
          >
            All
          </button>
          {tags.map((t) => (
            <button
              key={t.tag}
              type="button"
              onClick={() => setActiveTag(t.tag === activeTag ? null : t.tag)}
              className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
                activeTag === t.tag
                  ? 'border-indigo-400/70 bg-indigo-500/20 text-indigo-100'
                  : 'border-border/40 bg-background/50 hover:border-indigo-400/40 text-muted-foreground'
              }`}
              data-testid={`embed-marketplace-tag-${t.tag}`}
            >
              #{t.tag} <span className="text-muted-foreground/80 ml-0.5">{t.count}</span>
            </button>
          ))}
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 flex items-start gap-2" data-testid="embed-marketplace-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-[11px] text-muted-foreground italic mt-8">
          <Loader2 className="w-4 h-4 mx-auto mb-2 animate-spin" />
          Loading marketplace…
        </div>
      ) : visuals.length === 0 ? (
        <div className="text-center text-[11px] text-muted-foreground italic mt-8" data-testid="embed-marketplace-empty">
          No published templates yet. Publish one from the visual library to get started.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="embed-marketplace-grid">
          {visuals.map((v) => (
            <VisualTile
              key={v.visual_id}
              visual={v}
              canWrite={canWrite}
              canAdmin={canAdmin}
              onClone={clone}
              onToggleFeature={toggleFeature}
              onPreview={(vis) => {
                setPreviewVisual(vis);
                emit('marketplace.visual.previewed', { visual_id: vis.visual_id });
              }}
            />
          ))}
        </div>
      )}
      <PreviewModal
        visual={previewVisual}
        canWrite={canWrite}
        onClone={clone}
        onClose={() => setPreviewVisual(null)}
      />
    </div>
  );
}
